import type { QuizQuestion as QuizQuestionType } from '../types';

interface QuizQuestionProps {
  question: QuizQuestionType;
  selectedAnswers: string[];
  onAnswer: (answerId: string) => void;
  onNext: () => void;
}

export default function QuizQuestion({
  question,
  selectedAnswers,
  onAnswer,
  onNext,
}: QuizQuestionProps) {
  const isAnswered = selectedAnswers.length > 0;

  // Para perguntas com imagens (primeira pergunta)
  if (question.type === 'image-choice') {
    return (
      <div className="w-full max-w-4xl mx-auto px-4">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 text-gray-900">
          {question.question}
        </h2>
        <p className="text-sm sm:text-base text-center text-gray-600 mb-6 sm:mb-8 md:mb-12">
          Clique na imagem que melhor representa o seu caso
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8">
          {question.options.map((option) => (
            <button
              key={option.id}
              onClick={() => {
                onAnswer(option.id);
                setTimeout(onNext, 300);
              }}
              className={`group relative bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                selectedAnswers.includes(option.id)
                  ? 'ring-4 ring-red-600 shadow-2xl scale-105'
                  : 'shadow-lg hover:ring-2 hover:ring-red-400'
              }`}
            >
              <div className="w-full overflow-hidden bg-gray-50 flex items-center justify-center p-4">
                <img
                  src={option.image}
                  alt={option.label || 'Opção de calvície'}
                  className="w-full h-auto object-contain max-h-[300px] sm:max-h-[350px] md:max-h-[400px]"
                />
              </div>
              {selectedAnswers.includes(option.id) && (
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                  <i className="ri-check-line text-white text-lg sm:text-xl"></i>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Para perguntas normais (restante do quiz)
  return (
    <div className="w-full max-w-2xl mx-auto px-4">
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 sm:mb-8 md:mb-12 text-gray-900">
        {question.question}
      </h2>
      
      <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12">
        {question.options.map((option) => (
          <button
            key={option.id}
            onClick={() => onAnswer(option.id)}
            className={`w-full p-4 sm:p-6 rounded-xl sm:rounded-2xl text-left transition-all duration-300 text-base sm:text-lg font-medium ${
              selectedAnswers.includes(option.id)
                ? 'bg-red-600 text-white shadow-xl scale-105'
                : 'bg-white text-gray-900 hover:bg-red-50 hover:shadow-lg hover:scale-102 shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>{option.label}</span>
              {selectedAnswers.includes(option.id) && (
                <i className="ri-check-circle-fill text-2xl"></i>
              )}
            </div>
          </button>
        ))}
      </div>

      {isAnswered && (
        <div className="flex justify-center">
          <button
            onClick={onNext}
            className="px-8 sm:px-12 py-3 sm:py-4 bg-red-600 text-white rounded-full font-bold text-base sm:text-lg hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
          >
            Próxima Pergunta
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
}
